import type * as React from "react";

import { cn } from "../lib/cn";

/*
 * Path data vendored verbatim from @fortawesome/pro-solid-svg-icons@6.7.2
 * because the kit takes no icon dependency (DESIGN.md). Pro-licensed: never
 * ship this to a public package. Add a glyph only when a kit component needs
 * one the app already draws with FontAwesome.
 */
const TRIANGLE_EXCLAMATION =
  "M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z";

// -0.125em is FontAwesome's baseline nudge, matching the app's icons.
const GLYPH_SHAPE =
  "inline-block h-[1em] w-auto shrink-0 fill-current align-[-0.125em]";

export interface IconProps extends Omit<
  React.SVGAttributes<SVGSVGElement>,
  "children" | "viewBox"
> {
  className?: string;
  ref?: React.Ref<SVGSVGElement>;
}

interface GlyphProps extends IconProps {
  viewBox: string;
  d: string;
}

function labelled(rest: IconProps) {
  return rest["aria-label"] != null || rest["aria-labelledby"] != null;
}

/** aria-hidden unless given `aria-label`/`aria-labelledby`, then a labelled `img`. */
function Glyph({ viewBox, d, className, ...rest }: GlyphProps) {
  const named = labelled(rest);

  return (
    <svg
      viewBox={viewBox}
      focusable="false"
      role={named ? "img" : undefined}
      aria-hidden={named ? undefined : true}
      {...rest}
      className={cn(GLYPH_SHAPE, className)}
    >
      <path d={d} />
    </svg>
  );
}

export function TriangleExclamationIcon(props: IconProps) {
  return <Glyph viewBox="0 0 512 512" d={TRIANGLE_EXCLAMATION} {...props} />;
}

// Alina's 24-unit stroke set.
const STROKE_SHAPE =
  "inline-block size-[1em] shrink-0 fill-none stroke-current align-[-0.15em]";

interface StrokeGlyphProps extends IconProps {
  children: React.ReactNode;
}

function StrokeGlyph({
  className,
  strokeWidth = 2.2,
  children,
  ...rest
}: StrokeGlyphProps) {
  const named = labelled(rest);

  return (
    <svg
      viewBox="0 0 24 24"
      focusable="false"
      role={named ? "img" : undefined}
      aria-hidden={named ? undefined : true}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
      className={cn(STROKE_SHAPE, className)}
    >
      {children}
    </svg>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </StrokeGlyph>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </StrokeGlyph>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </StrokeGlyph>
  );
}

export function CrossIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </StrokeGlyph>
  );
}

export function ExpandIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </StrokeGlyph>
  );
}

export function CompressIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
    </StrokeGlyph>
  );
}

export function CheckCircleIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.5 2.5L16 9.5" />
    </StrokeGlyph>
  );
}

export function DotCircleIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" className="fill-current" stroke="none" />
    </StrokeGlyph>
  );
}

export function QuestionCircleIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7M12 17h.01" />
    </StrokeGlyph>
  );
}

export function CrossCircleIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" />
    </StrokeGlyph>
  );
}

export function ReceiptIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </StrokeGlyph>
  );
}

export function PinIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M12 21s-7-4.5-7-10a7 7 0 0 1 14 0c0 5.5-7 10-7 10z" />
      <circle cx="12" cy="11" r="2.5" />
    </StrokeGlyph>
  );
}

export function FlaskIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M9 3h6M10 3v6l-5.5 9.5A2 2 0 0 0 6.2 21h11.6a2 2 0 0 0 1.7-2.5L14 9V3" />
      <path d="M7.5 15h9" />
    </StrokeGlyph>
  );
}

export function FileTextIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6M8 13h8M8 17h5" />
    </StrokeGlyph>
  );
}

export function MessageIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.5-4.5A8 8 0 1 1 21 12z" />
    </StrokeGlyph>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M15 6l-6 6 6 6" />
    </StrokeGlyph>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M9 6l6 6-6 6" />
    </StrokeGlyph>
  );
}

export function SidebarIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M9 4v16" />
    </StrokeGlyph>
  );
}

export function BoardIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="9.5" y="4" width="5" height="11" rx="1" />
      <rect x="16" y="4" width="5" height="7" rx="1" />
    </StrokeGlyph>
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </StrokeGlyph>
  );
}

export function EnvelopeIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 6.5 12 13l8.5-6.5" />
    </StrokeGlyph>
  );
}

export function ChecklistIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 12l3 3 5-6" />
    </StrokeGlyph>
  );
}

export function GearIcon(props: IconProps) {
  return (
    <StrokeGlyph {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </StrokeGlyph>
  );
}
